// User-facing help content — authored as typed data (no markdown dependency; mirrors the
// OnboardingStep model). The HelpPanel renders the topic for the current route (via `match`)
// plus a searchable, browsable list of all topics. Keep copy task-oriented and plain-spoken.
//
// ORDERING MATTERS: topicForPath returns the FIRST topic whose `match` hits, so more specific
// routes (e.g. /jobs/new, /settings/developers) MUST appear before their generic parents
// (/jobs, /settings). The array below is ordered with that rule in mind.

export interface HelpField {
  name: string;
  required?: boolean;
  note: string;
}
export interface HelpStep {
  title: string;
  detail: string;
}
export interface HelpQA {
  q: string;
  a: string;
}
export interface HelpTopic {
  key: string;
  title: string;
  /** Pathname prefix(es) this topic is the contextual help for. First match wins,
   *  so more specific routes MUST be ordered before their generic parents. */
  match: RegExp;
  intro: string;
  /** The primary how-to walkthrough, rendered as a numbered list. */
  steps: HelpStep[];
  /** Every form field on the page, marked required/optional with the why. */
  fields?: HelpField[];
  /** "How do I…" scenarios beyond the happy path. */
  useCases?: HelpStep[];
  /** Q&A for gotchas, limits, and things people commonly wonder. */
  faqs?: HelpQA[];
  /** Short call-outs. Both `tips` and the legacy single `tip` render together. */
  tips?: string[];
  tip?: string;
}

export const HELP_TOPICS: HelpTopic[] = [
  // ─────────────────────────────── Getting started ───────────────────────────────
  {
    key: "getting-started",
    title: "Getting started",
    match: /^\/$/,
    intro: "RecruitTrucker brings hiring, freight, dispatch, fleet management, and compliance together on one verified account. Here's the quickest path to value — and everything you can do once you're set up.",
    steps: [
      { title: "Create a profile", detail: "Pick the profile that fits you — Company, Recruiter, Broker/Dispatcher, or Driver. You can hold more than one; each unlocks different features." },
      { title: "Verify your identity", detail: "A quick ID + selfie check by Stripe unlocks posting, applying, and bidding, and puts a trust badge on everything you do." },
      { title: "Post or browse", detail: "Companies and brokers post jobs and loads; drivers and carriers browse, apply, and bid. The search bar up top filters by lane, equipment, pay, and more." },
      { title: "Run your operation", detail: "Beyond the marketplace, manage your fleet, maintenance, fuel/IFTA, HOS logbook, compliance documents, CRM leads, and your professional network — all here." },
      { title: "Try Test mode first", detail: "New here? Flip the Live/Test switch in the top bar to explore a private sandbox pre-loaded with sample data — nothing you do touches real users or your card." },
    ],
    useCases: [
      { title: "I'm a driver looking for work", detail: "Create a Driver profile, verify, then browse Jobs and apply, or browse Loads and bid as an owner-operator." },
      { title: "I'm a carrier hauling freight", detail: "Verify, browse Loads and bid, then use Dispatch to track awarded shipments and Fleet-OS to manage your trucks." },
      { title: "I'm a company hiring drivers", detail: "Post a Job, review applicants in your Talent Pipeline, and message candidates directly." },
      { title: "I'm a broker moving freight", detail: "Post Loads, award the best bid, and open a shipment in Dispatch with a public tracking link for your customer." },
    ],
    tips: [
      "The setup checklist on your dashboard walks you through the essentials in order.",
      "Every page has its own help — open this panel anywhere and it shows guidance for exactly where you are.",
      "Use the search box at the top of this panel to jump straight to a topic (try 'IFTA', 'bid', or '2FA').",
    ],
  },

  // ─────────────────────────────── Identity & profiles ───────────────────────────────
  {
    key: "verify",
    title: "Verify your identity",
    match: /^\/verify/,
    intro: "Verification proves you're a real person and unlocks posting, applying, and bidding. Your ID images are handled by Stripe's secure hosted flow and are never stored by RecruitTrucker.",
    steps: [
      { title: "Start verification", detail: "Click Start verification — you'll be redirected to Stripe's hosted identity flow, then automatically back here." },
      { title: "Scan your ID + take a selfie", detail: "Have a government ID ready (driver's license, state ID, or passport). The selfie is matched against your ID photo to prove it's you." },
      { title: "Wait a moment", detail: "You'll return here automatically; the status flips to Verified within a few seconds of approval. If it's still pending, refresh in a minute." },
      { title: "Add credentials (optional)", detail: "Upload a CDL, DOT/MC authority, or medical card for an admin review — an extra trust badge that helps you get hired and approved faster." },
    ],
    faqs: [
      { q: "Why do I have to verify?", a: "It keeps scammers and ghost postings off the platform. A verified badge is what lets counterparties trust you enough to hire, bid, or send freight." },
      { q: "Is my ID stored?", a: "No. The document scan and selfie are processed by Stripe Identity. RecruitTrucker only receives a pass/fail result and your verified name — never the images." },
      { q: "It said 'Could not start verification.'", a: "That's usually a temporary hiccup — try again in a moment. If it persists, open a support ticket. In Test mode, verification is simulated and always passes." },
      { q: "How long does it take?", a: "The scan takes about a minute; approval is usually instant but can take a few minutes at busy times." },
    ],
    tip: "In Test mode, verification is simulated — your sandbox profile is treated as verified so you can try every feature without a real ID check.",
  },
  {
    key: "profiles",
    title: "Your profiles",
    match: /^\/profiles/,
    intro: "One account can hold several profiles — Company, Recruiter, Broker/Dispatcher, and Driver. Each unlocks different features, and you always act as whichever one you've selected.",
    steps: [
      { title: "Add a profile", detail: "Create a profile for each role you play. A carrier that also hires might keep both a Company and a Driver profile." },
      { title: "Fill it out", detail: "Add a display name, headline, photo, and details. A complete profile earns more trust and better matches." },
      { title: "Set a default", detail: "Your default profile is the one you post, apply, and bid as unless you switch. Mark the one you use most." },
      { title: "Switch anytime", detail: "Use the profile switcher to change which identity you're acting as — boards, applications, and messages update to match." },
    ],
    fields: [
      { name: "Profile type", required: true, note: "Company, Recruiter, Broker/Dispatcher, or Driver — determines which features and boards you see." },
      { name: "Display name", required: true, note: "Your company or personal name as others will see it." },
      { name: "Headline", note: "A one-line pitch (e.g. 'OTR reefer · 6 yrs · Hazmat'). Shows on the board and directory." },
      { name: "Photo / logo", note: "Optional but strongly recommended — profiles with a photo get far more responses." },
    ],
    useCases: [
      { title: "I do two jobs", detail: "Add a second profile of the other type. Switch between them from the profile menu — each keeps its own postings and applications." },
      { title: "I hire and I drive", detail: "Keep a Company profile for posting jobs and a Driver profile for applying to loads or jobs elsewhere." },
    ],
    faqs: [
      { q: "Do my profiles share reputation?", a: "Yes. Verification and reviews are tracked at the account level, so all your profiles carry the same trust badges." },
      { q: "How many profiles can I have?", a: "As many as the roles you actually play. Most people need one or two." },
    ],
    tip: "In Test mode you get separate sandbox profiles, so experimenting never touches your real profile or reputation.",
  },

  // ─────────────────────────────── Jobs (hiring) ───────────────────────────────
  {
    key: "post-job",
    title: "Post a job",
    match: /^\/jobs\/new/,
    intro: "Post a driver-hiring job to reach verified drivers. Only a title is strictly required — but the more detail you add, the better your applicant matches.",
    steps: [
      { title: "Give it a clear title", detail: "e.g. 'OTR Company Driver — Home Weekly'. This is the first thing drivers see on the board." },
      { title: "Set pay + route", detail: "Pay range, pay type, route type, and home time let drivers self-select — you'll get more relevant applicants and fewer tire-kickers." },
      { title: "Add requirements", detail: "CDL class, endorsements, and experience filter out mismatches. Leave blank anywhere you're flexible." },
      { title: "Publish & manage", detail: "Your job goes live immediately. Track and message applicants from 'My Jobs' and the Talent Pipeline." },
    ],
    fields: [
      { name: "Title", required: true, note: "The only required field. Keep it specific — role + a hook like home time." },
      { name: "Employment type", note: "Company driver, owner-operator, lease, etc. Helps drivers know if it fits." },
      { name: "Pay min / max", note: "Optional but strongly recommended — postings with pay get far more applies." },
      { name: "Pay type", note: "Weekly, per-mile, percentage, salary. Sets expectations up front." },
      { name: "Route / home time", note: "OTR, regional, local, dedicated. One of the top things drivers filter on." },
      { name: "Equipment", note: "Dry van, reefer, flatbed, etc. Optional — set it if the role is equipment-specific." },
      { name: "CDL class / endorsements", note: "Optional. Only set these if they're hard requirements, or you'll shrink your pool." },
      { name: "Location", note: "Optional. Enables distance-based search for drivers near the terminal." },
      { name: "Description", note: "Optional. Sell the role — benefits, freight, lanes, and what makes you a good place to drive." },
    ],
    useCases: [
      { title: "Edit or pause a posting", detail: "Open the job from 'My Jobs' to edit details, or close it when the seat is filled." },
      { title: "Review applicants", detail: "Applicants appear under the job and in your Talent Pipeline, grouped by stage — open one to read their profile and message them." },
    ],
    faqs: [
      { q: "Do I have to be verified to post?", a: "Yes — posting requires a verified account so drivers know the job is real." },
      { q: "Is posting free?", a: "Browsing, applying, and bidding are always free. Posting is a Pro feature once we pass the founding-member cap; founding members (the first 1,000) post free for their first year. It's always free in Test mode." },
    ],
    tip: "Posts with a pay range and honest home-time info get dramatically more (and better-fit) applicants.",
  },
  {
    key: "jobs-apply",
    title: "Browse & apply to jobs",
    match: /^\/jobs/,
    intro: "Find driving jobs and apply with your Driver profile. The board shows pay, route, and home time up front so you can scan fast.",
    steps: [
      { title: "Search + filter", detail: "Filter by equipment, route type, CDL class, and a location radius to surface good fits." },
      { title: "Open a job", detail: "Read the full description, pay, and requirements before applying." },
      { title: "Apply", detail: "Add a short, specific cover note and submit. You apply as your Driver profile." },
      { title: "Track & follow up", detail: "Watch statuses under 'My Applications' and message the recruiter directly to stand out." },
    ],
    useCases: [
      { title: "Save a job for later", detail: "Bookmark jobs you're interested in so you can come back and apply when you're ready." },
      { title: "See my applications", detail: "'My Applications' lists everything you've applied to and where each one stands." },
      { title: "Manage jobs I posted", detail: "If you're the one hiring, 'My Jobs' is where you edit postings and review applicants." },
    ],
    faqs: [
      { q: "Why can't I apply?", a: "Applying requires a verified account and a Driver profile selected. Verify under 'Verify identity' and switch to your Driver profile." },
      { q: "Will the recruiter see my profile?", a: "Yes — your Driver profile and verification badge go with your application, so keep the profile current." },
    ],
    tip: "A two-sentence cover note that names the lane or equipment you want beats a blank application every time.",
  },

  // ─────────────────────────────── Loads (freight) ───────────────────────────────
  {
    key: "post-load",
    title: "Post a load",
    match: /^\/loads\/new/,
    intro: "Post freight to reach verified carriers. Origin and destination are required; everything else sharpens your carrier matches and the quality of bids you get.",
    steps: [
      { title: "Enter origin + destination", detail: "City, State for each. These drive lane search and the distance/ETA features." },
      { title: "Describe the freight", detail: "Equipment type, full or partial, commodity, and weight tell carriers whether they can haul it." },
      { title: "Set the rate", detail: "A flat rate or 'negotiable'. Adding a target rate attracts more — and more realistic — bids." },
      { title: "Add dates & publish", detail: "Pickup and delivery dates help carriers plan. Publish, then award the best bid when it comes in." },
    ],
    fields: [
      { name: "Origin / Destination", required: true, note: "Both required. Use 'City, ST'. Powers lane search, distance, and ETA." },
      { name: "Equipment type", note: "Dry van, reefer, flatbed, etc. Optional but recommended — filters to carriers who can haul it." },
      { name: "Full / partial", note: "Whether it's a full truckload or LTL/partial." },
      { name: "Commodity / weight", note: "What's being hauled and how heavy. Helps carriers confirm they can take it legally." },
      { name: "Rate", note: "Optional. A number for a flat rate, or choose negotiable to invite offers." },
      { name: "Rate type", note: "Flat, per-mile, or negotiable." },
      { name: "Pickup / delivery dates", note: "Optional. Mark flexible if they can move — flexibility attracts more carriers." },
      { name: "Reference number", note: "Optional — your internal load or PO number for your own tracking." },
    ],
    useCases: [
      { title: "Award a bid", detail: "Open your load, compare carrier bids and their trust badges, and award the one you want. Then open Dispatch to track it." },
      { title: "Re-post or edit", detail: "Manage everything you've posted under 'My Loads' — edit details or repost a lane you run regularly." },
    ],
    faqs: [
      { q: "How do carriers find my load?", a: "It appears on the Loads board immediately and in lane/equipment searches. Verified, well-described loads with a rate get the most bids." },
      { q: "What happens after I award?", a: "Open a shipment in Dispatch to coordinate pickup, track GPS, advance status, and share a public tracking link with your customer." },
    ],
    tip: "Once a carrier's bid comes in, award it, then open Dispatch to turn it into a tracked shipment.",
  },
  {
    key: "loads-bid",
    title: "Browse & bid on loads",
    match: /^\/loads/,
    intro: "Find freight that fits your truck and place a bid. Use the filters to narrow by lane, equipment, and radius so you only see loads you'd actually run.",
    steps: [
      { title: "Filter the board", detail: "Search by origin/destination with a radius, equipment, and category to find matching loads." },
      { title: "Open a load", detail: "Review the freight, dates, and rate before bidding, and check the poster's trust badges." },
      { title: "Place your bid", detail: "Enter your rate and an optional note. You bid as a carrier or owner-operator profile." },
      { title: "Track your bids", detail: "See everything under 'My Bids'. If awarded, coordinate pickup via Dispatch and Messages." },
    ],
    fields: [
      { name: "Your rate", required: true, note: "What you'll haul it for. A note explaining availability or equipment helps you win." },
      { name: "Note", note: "Optional. Mention your ETA to pickup, equipment specifics, or willingness to negotiate." },
    ],
    useCases: [
      { title: "Bid on a negotiable load", detail: "Enter your number and a short note — negotiable loads expect an offer, so lead with a fair rate." },
      { title: "See if I won", detail: "'My Bids' shows each bid's status. An awarded bid moves the load into Dispatch for tracking." },
    ],
    faqs: [
      { q: "Why can't I bid?", a: "Bidding requires a verified account. Verify first; in Test mode you can bid freely on the sample loads." },
      { q: "Can I change my bid?", a: "Place your best number up front — check the current details before you submit. If the poster negotiates, continue in Messages." },
    ],
    tip: "You must be verified to bid. In Test mode you can bid freely on the sample loads to learn the flow.",
  },

  // ─────────────────────────────── Dispatch & tracking ───────────────────────────────
  {
    key: "dispatch",
    title: "Dispatch a shipment",
    match: /^\/dispatch/,
    intro: "After you award a bid on a load, open a shipment to coordinate pickup, track location live, advance status to delivery, and share a public tracking link with your customer.",
    steps: [
      { title: "Create the shipment", detail: "From an awarded load, start a shipment. Both you (poster) and the assigned carrier can see and update it." },
      { title: "Advance the status", detail: "Move it Dispatched → Picked up → In transit → Delivered as the load progresses." },
      { title: "Track live", detail: "The carrier shares GPS from the mobile app; you see the position, breadcrumb trail, and ETA on the map." },
      { title: "Share a tracking link", detail: "Generate a public link so your customer can follow the shipment without an account or seeing any PII." },
    ],
    useCases: [
      { title: "Set up geofences", detail: "Create yard/customer geofences so arrivals and departures are logged automatically as the truck crosses them." },
      { title: "Give the customer visibility", detail: "Send the public tracking link — it shows only live position, lane, and ETA." },
      { title: "Watch the whole fleet", detail: "The fleet view shows all your active shipments and their latest positions at once." },
    ],
    faqs: [
      { q: "Why can't I create a shipment?", a: "Only the load's poster can dispatch it, and the load needs an awarded bid first. Award a bid, then dispatch." },
      { q: "How does live tracking work?", a: "The assigned carrier shares GPS from the mobile app while the shipment is active. No app location means no live dot, but you can still advance status." },
    ],
    tip: "Geofences turn 'are you there yet?' calls into automatic arrival/departure timestamps.",
  },

  // ─────────────────────────────── Billing ───────────────────────────────
  {
    key: "billing",
    title: "Billing, Pro & payments",
    match: /^\/(billing|payments|invoices)/,
    intro: "Posting jobs and loads is a Pro feature (browsing, applying, and bidding are always free). Founding members — the first 1,000 — post free for their first year. This is where you manage your plan, card, and invoices.",
    steps: [
      { title: "Check your status", detail: "See whether you're a founding member, on Pro, or need to upgrade to post." },
      { title: "Upgrade to Pro", detail: "Start checkout to subscribe. Payments are handled securely by Stripe — we never see your card number." },
      { title: "Manage your plan", detail: "Open the customer portal to update your card, download invoices, or cancel." },
    ],
    faqs: [
      { q: "What's free vs. paid?", a: "Browsing jobs and loads, applying, and bidding are always free. Only posting requires Pro (once past the founding-member cap)." },
      { q: "What's a founding member?", a: "The first 1,000 accounts to post claim a founding slot and post free for one year. Your billing page shows your status and when the free year ends." },
      { q: "Where are my invoices?", a: "Inside the Stripe customer portal, reachable from 'Manage your plan'." },
    ],
    tip: "In Test mode, Pro is unlocked automatically and you're never charged — so you can practice posting freely.",
  },

  // ─────────────────────────────── Network & communication ───────────────────────────────
  {
    key: "network",
    title: "Find & connect with operators",
    match: /^\/(network|connections)/,
    intro: "The network directory lists verified drivers, carriers, recruiters, and brokers. Connect to build a trusted roster you can message, invite to loads, and re-hire.",
    steps: [
      { title: "Search the directory", detail: "Filter by role, equipment, and location to find operators that fit your lanes or hiring needs." },
      { title: "Send a connection request", detail: "Open a profile and request to connect. They'll see it under their connection requests." },
      { title: "Accept or decline", detail: "Requests to you appear on your Connections page — accept the ones you want in your network." },
      { title: "Work together", detail: "Connected operators are easy to message, invite to loads, and hire again." },
    ],
    faqs: [
      { q: "Do they have to accept?", a: "Yes — connections are mutual. Once they accept, you're in each other's network." },
      { q: "Can I connect with anyone?", a: "You can request anyone in the directory. Verified, complete profiles are far more likely to accept." },
    ],
    tip: "Build your network around operators you'd actually work with again — a small trusted roster beats a big cold one.",
  },
  {
    key: "messages",
    title: "Messaging",
    match: /^\/(messages|to\/)/,
    intro: "Private 1:1 messaging with anyone you're doing business with — applicants, carriers, recruiters, and marketplace sellers.",
    steps: [
      { title: "Start a conversation", detail: "Message from a job applicant, a load bid, a marketplace listing, or a profile — the thread opens on the Messages page." },
      { title: "Keep threads in one place", detail: "All your conversations live here, newest first, so nothing gets lost across email and phone." },
      { title: "Stay on-platform", detail: "Messaging through RecruitTrucker keeps a record tied to the deal, which helps if a dispute ever comes up." },
    ],
    faqs: [
      { q: "Who can message me?", a: "Operators you're interacting with — an applicant to your job, a bidder on your load, a buyer on your listing, or a connection." },
      { q: "Is there group chat?", a: "Yes — see Group chats for multi-party conversations like a load room with poster, carrier, and dispatcher." },
    ],
    tip: "Keeping negotiations in Messages means there's a clear record if you ever need to open a dispute.",
  },
  {
    key: "feed",
    title: "Activity feed",
    match: /^\/feed/,
    intro: "The feed shows what your network is doing on RecruitTrucker — recent posts, listings, and activity from the operators and communities you follow.",
    steps: [
      { title: "Follow the action", detail: "Scroll to see recent activity from your connections and the communities you've joined." },
      { title: "Grow your feed", detail: "Connect with more relevant operators and join communities to make the feed more useful." },
    ],
    tip: "A richer network means a more useful feed — spend a few minutes connecting with people you know.",
  },
  {
    key: "groups",
    title: "Group chats",
    match: /^\/groups/,
    intro: "Group chats are multi-party conversations — a load discussion between poster, carrier, and dispatcher, a team room, or any topic thread.",
    steps: [
      { title: "Create a group", detail: "Start a group and give it a name that says what it's for." },
      { title: "Add people", detail: "Invite the operators who need to be in the conversation." },
      { title: "Chat", detail: "Everyone in the group sees the thread and can reply — ideal for coordinating a shipment." },
    ],
    tip: "Spin up a group per shipment so poster, carrier, and dispatcher are all on the same page.",
  },
  {
    key: "communities",
    title: "Communities",
    match: /^\/communities/,
    intro: "Communities are topic groups — owner-operators, reefer haulers, new drivers, and more — where you can talk shop, ask questions, and share what works.",
    steps: [
      { title: "Browse groups", detail: "See the available communities with their member counts. Open one to read its posts." },
      { title: "Join", detail: "Join a community to unlock posting and commenting. You can leave anytime." },
      { title: "Post & comment", detail: "Start a discussion or reply to others. Keep it useful and on-topic." },
    ],
    faqs: [
      { q: "Why can't I post?", a: "You have to join a community before you can post or comment in it. Open the community and click Join." },
      { q: "Who sees my posts?", a: "Members of that community. In Test mode, your posts stay in your sandbox and aren't visible to real users." },
    ],
    tips: [
      "Be helpful and specific — the best communities run on real, practical answers.",
      "In Test mode you can practice posting; the community list is shared but your posts stay private to your sandbox.",
    ],
  },

  // ─────────────────────────────── Marketplace & auctions ───────────────────────────────
  {
    key: "marketplace",
    title: "Marketplace",
    match: /^\/marketplace/,
    intro: "Buy and sell trucks, trailers, equipment, and parts. Listings are classified ads — you contact the seller through on-platform messaging and arrange the deal directly.",
    steps: [
      { title: "Browse or search", detail: "Filter by category and keyword to find what you need." },
      { title: "List an item", detail: "Post a listing with a title, category, price, condition, and location. Only a title is required." },
      { title: "Contact the seller", detail: "Message the seller from a listing to ask questions or make an offer." },
      { title: "Mark it sold", detail: "When it's gone, set your listing to Sold or Removed from 'My listings'." },
    ],
    fields: [
      { name: "Title", required: true, note: "The only required field. Be specific — year, make, model." },
      { name: "Category", note: "Trucks, trailers, parts, services, etc. Powers browsing and filters." },
      { name: "Price", note: "Optional. Leave blank or mark negotiable if you're open to offers." },
      { name: "Condition", note: "New, used, for parts — sets buyer expectations." },
      { name: "Location", note: "Optional. Helps local buyers find you." },
      { name: "Description / photos", note: "Optional but sells the item — the more detail and pictures, the fewer tire-kicker questions." },
    ],
    faqs: [
      { q: "Does RecruitTrucker handle payment?", a: "No — the marketplace connects buyers and sellers. Arrange payment and pickup directly with the other party, and use normal caution for high-value deals." },
      { q: "How do buyers reach me?", a: "Through on-platform Messages from your listing, so there's a record of the conversation." },
    ],
    tip: "RecruitTrucker doesn't process marketplace payments — meet, inspect, and pay using methods you both trust.",
  },
  {
    key: "auctions",
    title: "Auctions",
    match: /^\/auctions/,
    intro: "Timed ascending auctions for trucks, trailers, and equipment. The highest bid when the clock runs out wins — as long as the seller's reserve is met.",
    steps: [
      { title: "Browse live auctions", detail: "See what's ending soon, the current price, and how many bids are in." },
      { title: "Place a bid", detail: "Bid at or above the minimum next bid. Bids are final and enforced in real time, so you can't be beaten by a stale number." },
      { title: "Sell your equipment", detail: "Create an auction with a starting price, an optional hidden reserve, a minimum increment, and an end time." },
      { title: "Close it out", detail: "You can close early as the seller, or anyone can finalize once it's ended. If the reserve wasn't met, there's no winner." },
    ],
    fields: [
      { name: "Title", required: true, note: "What you're selling — year, make, model." },
      { name: "Starting price", required: true, note: "The opening bid." },
      { name: "Reserve price", note: "Optional. The minimum you'll accept — hidden from bidders. No sale if it isn't met." },
      { name: "Min increment", note: "Optional. How much each bid must raise the price (defaults to $50)." },
      { name: "Ends in (hours)", required: true, note: "Auction length, from 1 to 720 hours (30 days)." },
    ],
    faqs: [
      { q: "Can I bid on my own auction?", a: "No — you can't bid on your own listing. That keeps prices honest." },
      { q: "What if the reserve isn't met?", a: "The auction ends with no winner and you keep the equipment. Set a realistic reserve to actually sell." },
      { q: "Can I bid in Test mode?", a: "Test mode is create-and-close for auctions — you can list and close your own sandbox auctions, but there are no shared sample auctions to bid on." },
    ],
    tip: "You must be verified to bid or sell in auctions.",
  },

  // ─────────────────────────────── Fleet-OS ───────────────────────────────
  {
    key: "fleet",
    title: "Fleet",
    match: /^\/fleet/,
    intro: "Your fleet is the single place to track every truck and trailer — its status, meters, and who's driving it. It's the backbone the rest of Fleet-OS (maintenance, fuel, logs) builds on.",
    steps: [
      { title: "Add a vehicle", detail: "Register each truck or trailer with a unit number and type." },
      { title: "Keep status current", detail: "Mark vehicles active, in-shop, or out of service so everyone sees the real picture." },
      { title: "Assign a driver", detail: "Link a driver to a vehicle so dispatch, logs, and inspections line up." },
      { title: "Track meters", detail: "Odometer and engine hours feed maintenance schedules and fuel/IFTA reporting automatically." },
    ],
    fields: [
      { name: "Unit number", required: true, note: "Your internal number/name for the vehicle — how you'll recognize it everywhere." },
      { name: "Type", note: "Tractor, trailer, straight truck, etc." },
      { name: "Odometer / engine hours", note: "Current meters. Keep them fresh — maintenance due-dates and IFTA MPG depend on them." },
      { name: "Status", note: "Active, in-shop, out of service. Drives what shows as available." },
    ],
    useCases: [
      { title: "Take a truck out of service", detail: "Set its status so it stops appearing as available and dispatch knows not to assign it." },
      { title: "Reassign a driver", detail: "Update the assigned driver when someone changes trucks so logs and inspections follow the right unit." },
    ],
    tip: "Accurate odometer/hours here are what make maintenance reminders and IFTA numbers trustworthy.",
  },
  {
    key: "maintenance",
    title: "Maintenance & diagnostics",
    match: /^\/maintenance/,
    intro: "Stay ahead of breakdowns with preventive schedules, a full service history, and fault-code tracking for each vehicle.",
    steps: [
      { title: "Set a schedule", detail: "Create a preventive task (e.g. oil change) with an interval by miles, engine hours, or time." },
      { title: "Watch what's due", detail: "The board shows each schedule as upcoming, due, or overdue based on the vehicle's current meter." },
      { title: "Log a service", detail: "Record completed work with cost, vendor, and notes. Completing a scheduled task resets its clock automatically." },
      { title: "Resolve fault codes", detail: "Track diagnostic fault codes and mark them resolved once fixed." },
    ],
    fields: [
      { name: "Vehicle", required: true, note: "Which truck/trailer the schedule or record is for." },
      { name: "Task", required: true, note: "What's being done — 'Oil & filter', 'DOT inspection', 'Brakes'." },
      { name: "Interval type + value", required: true, note: "By miles, engine hours, or time (e.g. every 15,000 mi or 90 days)." },
      { name: "Cost / vendor", note: "Optional on a service record — useful for cost tracking and warranty history." },
      { name: "Odometer / hours at service", note: "Optional. Recording it resets the schedule clock and updates the vehicle's meter." },
    ],
    faqs: [
      { q: "How does 'due' get calculated?", a: "From the vehicle's current odometer/engine hours (or the calendar) versus the interval you set. Keep meters current in Fleet for accuracy." },
      { q: "Do I have to log against a schedule?", a: "No — you can log a one-off service too. Linking it to a schedule is what resets that schedule's clock." },
    ],
    tip: "Set schedules once and let the board tell you what's due — no more spreadsheets or guesswork.",
  },
  {
    key: "fuel",
    title: "Fuel & IFTA",
    match: /^\/fuel/,
    intro: "Log fuel purchases and per-state miles, and RecruitTrucker builds your IFTA quarter for you — gallons, miles, MPG, and net tax by jurisdiction.",
    steps: [
      { title: "Log fuel purchases", detail: "Record each fill-up with gallons, cost, jurisdiction, and vehicle. This drives your MPG and fuel-tax credits." },
      { title: "Enter miles by state", detail: "Set miles driven per jurisdiction for the quarter. One entry per state (and vehicle) — re-saving updates it." },
      { title: "Review the IFTA summary", detail: "Pick a quarter to see computed miles, gallons, MPG, and net tax owed or credited by state." },
    ],
    fields: [
      { name: "Gallons", required: true, note: "How much you bought — the core of both MPG and tax-paid credits." },
      { name: "Jurisdiction", required: true, note: "The state/province where you fueled. Determines which column the tax credit lands in." },
      { name: "Vehicle", note: "Optional but recommended — lets you see per-truck MPG." },
      { name: "Total cost", note: "Optional. Useful for cost-per-mile tracking." },
      { name: "Miles (per state)", required: true, note: "On the miles form: distance run in each jurisdiction for the quarter." },
    ],
    faqs: [
      { q: "Is this an official IFTA filing?", a: "No — it computes the numbers and net tax to make filing easy, but you still submit through your base jurisdiction." },
      { q: "Why is my MPG off?", a: "MPG depends on complete gallons and miles for the quarter. Missing fuel receipts or state miles skew it — log consistently." },
    ],
    tip: "Consistent logging all quarter turns IFTA filing into a five-minute job instead of a shoebox nightmare.",
  },
  {
    key: "assets",
    title: "Assets & trailers",
    match: /^\/assets/,
    intro: "Track trailers, containers, and other assets by a tag you can scan in the field — and always know who has each one and where it was last seen.",
    steps: [
      { title: "Register an asset", detail: "Add each asset with a unique tag, a type, and an optional label. The tag is how drivers scan it." },
      { title: "Check in / check out", detail: "Scan the tag to record a check-in or check-out with location and who did it." },
      { title: "See the history", detail: "Each asset keeps a timeline of check-ins, so you know its last known location and holder." },
    ],
    fields: [
      { name: "Tag", required: true, note: "A unique code (QR/barcode/plate) — must be unique within your account. This is what gets scanned." },
      { name: "Type", note: "Trailer, container, chassis, etc." },
      { name: "Label", note: "Optional friendly name to recognize it quickly." },
    ],
    faqs: [
      { q: "It says the tag already exists.", a: "Tags must be unique per account. Pick a different code, or find the existing asset with that tag." },
      { q: "How do drivers scan?", a: "From the mobile app they scan the tag to check the asset in or out — location and identity are recorded automatically." },
    ],
    tip: "Unique, physical tags on every trailer mean you never lose track of who has what.",
  },
  {
    key: "logbook",
    title: "Log book (Hours of Service)",
    match: /^\/logbook/,
    intro: "Track Hours-of-Service duty status and clocks, and file DVIRs. This is a companion record to help you stay legal — not a certified ELD.",
    steps: [
      { title: "Set your duty status", detail: "Log Off-duty, Sleeper, Driving, or On-duty as your day changes. The clocks update from your entries." },
      { title: "Watch your clocks", detail: "See drive time, on-duty window, and 70-hour cycle remaining, plus any break needed or violations." },
      { title: "File a DVIR", detail: "Submit a pre-trip or post-trip inspection, noting any defects and whether the vehicle is safe to operate." },
      { title: "Export your log", detail: "Export a date range as a RODS-style CSV for your records." },
    ],
    fields: [
      { name: "Duty status", required: true, note: "Off-duty, Sleeper berth, Driving, or On-duty (not driving)." },
      { name: "Location / note", note: "Optional context for the status change — where you were, why." },
      { name: "DVIR: safe to operate", note: "On an inspection: whether the vehicle is safe to drive. Note any defects." },
    ],
    faqs: [
      { q: "Is this a legal ELD?", a: "No. It's a companion HOS record and DVIR log. For legal HOS compliance you still need a certified ELD — connect one under Telematics & ELD." },
      { q: "How are the clocks figured?", a: "From your logged duty-status changes over the trailing days, using standard HOS drive/on-duty/70-hour-cycle rules." },
    ],
    tip: "For legal Hours-of-Service compliance you still need a certified ELD — connect one under Telematics & ELD.",
  },
  {
    key: "telematics",
    title: "Telematics & ELD",
    match: /^\/telematics/,
    intro: "Bring HOS, GPS, and engine data from whatever ELD you already run into one place — by connecting a provider, an ingest webhook, or a RODS CSV import.",
    steps: [
      { title: "Connect a source", detail: "Register a connection for your ELD provider. Direct Motive/Samsara/Geotab OAuth is being wired up; the webhook and CSV import work today." },
      { title: "Feed in data", detail: "Point your ELD or aggregator at the ingest webhook using the connection's token, or import a RODS CSV directly." },
      { title: "See it unified", detail: "Records and a summary (last sync, counts by type) appear here regardless of which ELD they came from." },
    ],
    fields: [
      { name: "Provider", required: true, note: "Which ELD/telematics system this connection is for." },
      { name: "Display name", note: "Optional label to tell connections apart." },
      { name: "Ingest token", note: "Auto-generated secret your ELD/webhook uses to post data. Keep it secret; rotate it anytime." },
    ],
    faqs: [
      { q: "Which providers work now?", a: "The unified webhook and RODS-CSV import work with any source today. Direct OAuth for Motive/Samsara/Geotab is scaffolded and returns a 'configure credentials' status until activated." },
      { q: "What if my token leaks?", a: "Anyone with the ingest token can post records to your connection. Rotate it from the connection to invalidate the old one instantly." },
    ],
    tip: "Keep your ingest token secret — treat it like a password, and rotate it if you ever suspect it's exposed.",
  },

  // ─────────────────────────────── Compliance & safety ───────────────────────────────
  {
    key: "compliance",
    title: "Compliance vault",
    match: /^\/compliance/,
    intro: "Keep your compliance documents — insurance, authority, W-9, CDL, medical card, IFTA — in one secure place, and share a carrier packet with brokers in a click.",
    steps: [
      { title: "Add a document", detail: "Upload each document with its type and issue/expiry dates. Files are stored privately and served only via short-lived links." },
      { title: "Watch expirations", detail: "The vault flags documents expiring soon or already expired, so you renew before it costs you a load." },
      { title: "Share a carrier packet", detail: "Generate a public packet link that shows brokers your document list and statuses — without exposing the files themselves." },
    ],
    fields: [
      { name: "Document type", required: true, note: "Insurance, authority (MC), W-9, CDL, medical card, IFTA, etc." },
      { name: "File", required: true, note: "The document itself — stored in a private bucket, never public." },
      { name: "Issued / expires", note: "Dates that drive the expiring-soon and expired warnings." },
      { name: "Title", note: "Optional label to tell similar documents apart." },
    ],
    faqs: [
      { q: "Who can see my files?", a: "Only you. A shared carrier packet shows brokers the list and expiry status, not the underlying files, and never your sandbox/test documents." },
      { q: "How do the expiry warnings work?", a: "From the expiry dates you enter — the vault marks each document valid, expiring soon, or expired." },
    ],
    tip: "A complete, current packet gets you approved faster with brokers and shippers.",
  },
  {
    key: "incidents",
    title: "Safety incidents",
    match: /^\/incidents/,
    intro: "Record accidents, near-misses, and other safety incidents with details and photos — and tie them to a shipment when relevant.",
    steps: [
      { title: "Report an incident", detail: "Log what happened, when, and where. Attach photos or documents as evidence." },
      { title: "Link a shipment", detail: "If it happened on a load you were running, link the shipment so the record has full context." },
      { title: "Keep the record", detail: "A clear incident history supports insurance claims and shows a pattern of responsible operation." },
    ],
    fields: [
      { name: "What happened", required: true, note: "A clear description of the incident." },
      { name: "When / where", note: "Time and location — important for claims and follow-up." },
      { name: "Media", note: "Optional photos or documents. Only real image files are accepted, and they're stored privately." },
      { name: "Linked shipment", note: "Optional. Connect the incident to the load it happened on." },
    ],
    tip: "Document incidents promptly with photos — memories fade and insurers ask for specifics.",
  },

  // ─────────────────────────────── CRM & growth ───────────────────────────────
  {
    key: "crm",
    title: "CRM — leads & contacts",
    match: /^\/crm/,
    intro: "A lightweight CRM to track drivers, partners, and vendors through a pipeline, with follow-up tasks so nobody slips through the cracks.",
    steps: [
      { title: "Add contacts", detail: "Create a contact for each lead or partner, with a type and a status (new, contacted, qualified, won, lost)." },
      { title: "Move them along", detail: "Update status as the relationship progresses. The Pipeline view shows everyone by stage." },
      { title: "Set follow-up tasks", detail: "Add tasks with due dates so the next touch never gets forgotten." },
      { title: "Automate the busywork", detail: "Turn on Automations to fire follow-ups automatically when a new lead is added." },
    ],
    fields: [
      { name: "Name", required: true, note: "Who the contact is." },
      { name: "Contact type", note: "Driver lead, partner, vendor, or other — organizes your pipeline." },
      { name: "Status", note: "New, contacted, qualified, won, or lost. Drives the pipeline stages." },
      { name: "Email / phone", note: "Optional. Needed if you want to reach them via Marketing campaigns." },
      { name: "Notes", note: "Optional. Context for your next conversation." },
    ],
    faqs: [
      { q: "How is this different from the Talent Pipeline?", a: "The CRM tracks any lead/partner/vendor you add manually. The Talent Pipeline is automatic — it's the applicants across your job postings." },
      { q: "Can I bulk-message contacts?", a: "Yes — use Marketing to run email/SMS campaigns to your CRM contacts (respecting opt-outs)." },
    ],
    tip: "Log a next-step task on every contact — a CRM without follow-ups is just an address book.",
  },
  {
    key: "pipeline",
    title: "Talent pipeline",
    match: /^\/pipeline/,
    intro: "See every candidate across all your job postings, organized by stage, so you can move applicants forward without digging through each posting one by one.",
    steps: [
      { title: "Scan by stage", detail: "Candidates are grouped by where they are — applied, in review, hired, and so on." },
      { title: "Act quickly", detail: "Open a candidate to review their profile, message them, or advance them to the next stage." },
      { title: "Keep it moving", detail: "The best recruiters respond fast — a stalled pipeline loses good drivers to competitors." },
    ],
    tip: "Sort by 'applied' first thing each day — speed of first response is the #1 driver of who you land.",
  },
  {
    key: "marketing",
    title: "Marketing campaigns",
    match: /^\/marketing/,
    intro: "Run email and SMS campaigns to your CRM contacts — recruiting blasts, load alerts, or partner updates.",
    steps: [
      { title: "Pick your audience", detail: "Target contacts from your CRM, filtered by type or status." },
      { title: "Write the message", detail: "Compose an email or SMS. Keep it short, specific, and honest about the opportunity." },
      { title: "Send & track", detail: "Send the campaign and watch how it performs." },
    ],
    faqs: [
      { q: "Where do recipients come from?", a: "Your CRM contacts that have an email or phone. Add contact details in the CRM first." },
      { q: "What about opt-outs?", a: "Only message people who expect to hear from you, and honor opt-outs — it keeps your sender reputation (and deliverability) healthy." },
    ],
    tip: "A short, specific message to 50 the-right-people beats a generic blast to 500.",
  },
  {
    key: "automations",
    title: "Automations",
    match: /^\/automations/,
    intro: "Rules that run for you in the background — like automatically creating a follow-up task whenever a new lead is added to your CRM.",
    steps: [
      { title: "Create a rule", detail: "Choose a trigger (e.g. a new contact) and the action it should take (e.g. add a follow-up task)." },
      { title: "Let it run", detail: "Once active, the rule fires on its own every time the trigger happens." },
      { title: "Adjust as you go", detail: "Turn rules on or off as your workflow changes." },
    ],
    tip: "Start with one rule — auto-create a follow-up when a lead is added — and build from there.",
  },
  {
    key: "referrals",
    title: "Refer & earn",
    match: /^\/referrals/,
    intro: "Invite other operators with your personal referral link and earn rewards when they join and get active.",
    steps: [
      { title: "Grab your link", detail: "Copy your unique referral link from this page." },
      { title: "Share it", detail: "Send it to drivers, carriers, and recruiters who'd benefit from RecruitTrucker." },
      { title: "Earn", detail: "When people sign up and verify through your link, you get credit." },
    ],
    tip: "Share your link where operators already gather — driver groups, dispatch chats, and yard break rooms.",
  },
  {
    key: "integrations",
    title: "API keys & integrations",
    match: /^\/integrations/,
    intro: "Create API keys to connect RecruitTrucker to your own tools and systems.",
    steps: [
      { title: "Create a key", detail: "Generate a new API key. Copy it right away — for your security it's shown only once." },
      { title: "Use it securely", detail: "Send it from your server, never from a browser or mobile app where it could be exposed." },
      { title: "Rotate if leaked", detail: "Revoke and regenerate a key anytime you suspect it's been exposed." },
    ],
    fields: [
      { name: "Key name", note: "A label so you remember what each key is used for." },
    ],
    tip: "Treat API keys like passwords — anyone with a key can act as your account.",
  },
  {
    key: "analytics",
    title: "Analytics",
    match: /^\/analytics/,
    intro: "Lifetime KPIs and trends for your account — postings, applications, bids, and how your activity is performing over time.",
    steps: [
      { title: "Read your KPIs", detail: "See the headline numbers for your account at a glance." },
      { title: "Spot trends", detail: "Use the charts to see what's growing and what's stalling, then adjust." },
    ],
    tip: "Watch your apply/bid-to-award ratio — if it's dropping, your postings or pricing may need a tune-up.",
  },

  // ─────────────────────────────── Account, security & support ───────────────────────────────
  {
    key: "test-mode",
    title: "Using Test mode",
    match: /^\/settings\/developers/,
    intro: "Test mode is your private sandbox. It looks and works exactly like the real app but uses fake, isolated data — perfect for learning any feature without any risk.",
    steps: [
      { title: "Switch to Test", detail: "Use the Live/Test toggle in the top bar (or here in Settings → Developers). Starter sample data is set up for you automatically the first time." },
      { title: "Explore freely", detail: "Post jobs and loads, bid, apply, dispatch, message, and try Fleet-OS, CRM, marketplace, and communities. Nothing touches the live marketplace, other users, or your card." },
      { title: "Start fresh anytime", detail: "Made a mess? Click 'Repopulate test data' to wipe your sandbox and get a clean set of samples back." },
      { title: "Switch back to Live", detail: "Flip the toggle to Live (or use the banner's Exit button) to return to your real account and data." },
    ],
    faqs: [
      { q: "Can other people see my test data?", a: "No. Your test data is isolated to you — even other users in their own Test mode can't see it. Everyone shares a common set of sample listings to interact with." },
      { q: "Will I be charged in Test mode?", a: "Never. Pro is unlocked automatically and verification is simulated, so you can practice posting and everything else for free." },
      { q: "Does 'Repopulate' delete my real data?", a: "No — it only wipes and rebuilds your Test-mode sandbox. Your live account and data are untouched." },
    ],
    tips: [
      "The amber banner at the top always tells you when you're in Test mode.",
      "Test mode is the safest way to learn a new feature — break things freely, then reset.",
    ],
  },
  {
    key: "security",
    title: "Account security & 2FA",
    match: /^\/(settings\/security|security)/,
    intro: "Protect your account with two-factor authentication and keep an eye on where you're signed in.",
    steps: [
      { title: "Turn on 2FA", detail: "Follow the wizard to add an authenticator app (TOTP) or email one-time codes. You'll enter a code at login on top of your password." },
      { title: "Keep a backup", detail: "Set up both methods where you can, so a lost phone doesn't lock you out." },
      { title: "Review active sessions", detail: "See where your account is signed in and sign out anything you don't recognize." },
    ],
    faqs: [
      { q: "Which 2FA should I use?", a: "An authenticator app (TOTP) is the most secure and works offline. Email one-time codes are a convenient backup." },
      { q: "I lost my authenticator.", a: "Sign in with your backup method (email codes) if you set one up. If you're fully locked out, contact support to recover access." },
    ],
    tip: "2FA is the single biggest thing you can do to protect your account — and your reputation.",
  },
  {
    key: "organizations",
    title: "Teams & organizations",
    match: /^\/(organizations|orgs)/,
    intro: "Create a team so multiple people can act on one company's behalf, each with a scoped role — instead of sharing a single login.",
    steps: [
      { title: "Create an organization", detail: "Set up a team for your company." },
      { title: "Invite members", detail: "Invite people by email and assign each a role that limits what they can do." },
      { title: "Manage access", detail: "Change roles or remove members as your team changes — no shared passwords required." },
    ],
    faqs: [
      { q: "Why not just share a login?", a: "Scoped roles mean each person only gets the access they need, and you get an audit trail of who did what — safer and cleaner as you grow." },
      { q: "Can I limit what someone can do?", a: "Yes — roles are scoped, so you can give a dispatcher different access than a billing admin." },
    ],
    tip: "Give each person the least access they need — safer, and it keeps a clear record of who did what.",
  },
  {
    key: "disputes",
    title: "Disputes",
    match: /^\/disputes/,
    intro: "If a deal goes wrong, open a dispute so the RecruitTrucker team can review the record and help resolve it.",
    steps: [
      { title: "Open a dispute", detail: "Describe what happened and attach any evidence — messages, documents, or photos." },
      { title: "Let it be reviewed", detail: "Our team reviews the on-platform record, which is why keeping deals and messages here matters." },
      { title: "Follow up", detail: "Track the status and respond to any questions from the reviewer." },
    ],
    tip: "Disputes are much easier to resolve when the negotiation happened in Messages — keep deals on-platform.",
  },
  {
    key: "support",
    title: "Support",
    match: /^\/support/,
    intro: "Get help from the RecruitTrucker team. Open a ticket and we'll get back to you.",
    steps: [
      { title: "Open a ticket", detail: "Describe your issue with as much detail as you can — screenshots help a lot." },
      { title: "Track replies", detail: "Follow the conversation and add more information if we ask for it." },
    ],
    tip: "For quick how-tos, search this help panel first — the answer to your question may already be here.",
  },
  {
    key: "notifications",
    title: "Notifications",
    match: /^\/notifications/,
    intro: "One place for activity across your applications, bids, messages, and account — so nothing important slips by.",
    steps: [
      { title: "Review what's new", detail: "Scan recent notifications and open any that need action." },
      { title: "Stay on top of deals", detail: "New applicants, bids, and messages show up here — responding fast wins business." },
    ],
    tip: "Check notifications first thing — a quick reply to a new applicant or bid often decides who wins the deal.",
  },
  {
    key: "settings",
    title: "Account settings",
    match: /^\/settings/,
    intro: "Manage your account — profile details, notifications, appearance (light/dark theme), security, and the developer/Test-mode tools.",
    steps: [
      { title: "Update your account", detail: "Edit your name, contact info, and preferences." },
      { title: "Set your theme", detail: "Choose Light, Dark, or System to match your device." },
      { title: "Tighten security", detail: "Open the Security section to set up 2FA and review active sessions." },
      { title: "Explore Test mode", detail: "The Developers section holds the Live/Test toggle and 'Repopulate test data'." },
    ],
    tip: "New here? The Developers section's Test mode lets you try every setting and feature risk-free.",
  },
];

/** The contextual topic for a pathname, or null. First match wins (specific routes first). */
export function topicForPath(pathname: string): HelpTopic | null {
  return HELP_TOPICS.find((t) => t.match.test(pathname)) ?? null;
}
